import useOcrSearch from "../custom-hooks/useOcrSearch";
import usePageSegmentation from "../custom-hooks/usePageSegmentation";
import OcrSearchHighlights, { OcrSearchStatus } from "./OcrSearchHighlights";
import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Container, Dropdown, DropdownButton, OverlayTrigger, Tooltip, Form } from "react-bootstrap";
import {
  downloadTextFile,
  generateArabicTranscript,
  generateTranslatedTranscript,
} from "../utils/helpers";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faLanguage,
  faCopy,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

import { ReactComponent as Calender } from "../assets/icons/calendar.svg";
import { ReactComponent as Compass } from "../assets/icons/compass.svg";
import { ReactComponent as Multiple } from "../assets/icons/multiple-documents.svg";
import { ReactComponent as Document } from "../assets/icons/document.svg";
import { ReactComponent as Close } from "../assets/icons/close.svg";

import { toast } from "react-toastify";

import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";
import { toolbarPlugin } from "@react-pdf-viewer/toolbar";

import "../styles/pdf-modal.css";
import { segmentationAPI } from "../axios/segmentation";
import { feedbackAPI } from "../axios/feedback_axios";

const TOAST_IDS = {
  feedbackSuccess: "pdf-feedback-success",
  feedbackError: "pdf-feedback-error",
  feedbackMissingDetails: "pdf-feedback-missing-details",
};

const FEEDBACK_TYPES = [
  { value: "transcription_error", label: "Transcription error" },
  { value: "translation_error", label: "Translation error" },
  { value: "incorrect_description", label: "Incorrect description" },
  { value: "image_quality", label: "Image not clear" },
  { value: "box_placement", label: "Box placement issue" },
  { value: "other", label: "Other feedback" },
];

const TooltipActionButton = ({ tooltipMessage, icon, children, className = "", ...buttonProps }) => (
  <OverlayTrigger
    placement="top"
    delay={{ show: 250, hide: 400 }}
    overlay={<Tooltip id="action-btn-tooltip">{tooltipMessage}</Tooltip>}
  >
    <Button className={`ActionButton ${className}`.trim()} {...buttonProps}>
      {icon ? <FontAwesomeIcon icon={icon} /> : null}
      {children}
    </Button>
  </OverlayTrigger>
);

const TranslationControls = ({ selectedLanguage, handleLanguageChange, handleTranslateClick }) => (
  <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "10px" }}>
    <DropdownButton
      variant="outline-primary"
      title={`Language: ${selectedLanguage.toUpperCase()}`}
      onSelect={handleLanguageChange}
      className="LanguageDropdown"
    >
      <Dropdown.Item eventKey="en">English (EN)</Dropdown.Item>
      <Dropdown.Item eventKey="fr">French (FR)</Dropdown.Item>
      <Dropdown.Item eventKey="pt">Portuguese (PT)</Dropdown.Item>
      <Dropdown.Item eventKey="es">Spanish (ES)</Dropdown.Item>
    </DropdownButton>

    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id="translate-beta-tooltip">
          Beta — This feature is under testing, use your own discretion
        </Tooltip>
      }
    >
      <Button variant="primary" className="TranslateButton" onClick={handleTranslateClick}>
        <FontAwesomeIcon icon={faLanguage} /> Translate
      </Button>
    </OverlayTrigger>
  </div>
);

const TranslationModal = ({ text, onClose, onClickTranslate }) => {
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [textDisplayed, setTextDisplayed] = useState(text);
  const hasText = typeof textDisplayed === "string" && textDisplayed.trim().length > 0;

  useEffect(() => {
    setTextDisplayed(text);
  }, [text]);

  const handleLanguageChange = (lang) => setSelectedLanguage(lang);

  const handleTranslateClick = async () => {
    const res = await onClickTranslate(selectedLanguage.toUpperCase());
    if (res) setTextDisplayed(res);
  };

  const handleCopy = () => {
    if (!textDisplayed) return;
    navigator.clipboard.writeText(textDisplayed);
    toast.success("Copied to clipboard!", { position: "top-center", autoClose: 2000 });
  };

  const handleDownload = () => {
    const content = textDisplayed;

    if (!content || content.trim().length === 0) {
      toast.warn("No content to download.", { position: "top-center", autoClose: 3000 });
      return;
    }

    const isOriginal = textDisplayed === text;
    const transcript = isOriginal
      ? generateArabicTranscript(content)
      : generateTranslatedTranscript(content, selectedLanguage);

    const filename = isOriginal
      ? "arabic_transcript.txt"
      : `translated_transcript_${selectedLanguage}.txt`;

    downloadTextFile(transcript, filename);
  };

  return (
    <Container className="d-flex flex-column" style={{ padding: "16px", height: "100%" }}>
      <TranslationControls
        selectedLanguage={selectedLanguage}
        handleLanguageChange={handleLanguageChange}
        handleTranslateClick={handleTranslateClick}
      />

      <div style={{ marginTop: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
        <TooltipActionButton
          tooltipMessage="Download transcript"
          variant="outline-primary"
          size="sm"
          onClick={handleDownload}
          icon={faDownload}
          style={{ width: "30px", height: "30px", padding: 0 }}
        />

        <TooltipActionButton
          tooltipMessage="Copy to clipboard"
          variant="outline-primary"
          size="sm"
          onClick={handleCopy}
          icon={faCopy}
          style={{ width: "30px", height: "30px", padding: 0 }}
        />

        <Button
          variant="outline-secondary"
          size="sm"
          onClick={onClose}
          style={{
            marginLeft: "auto",
            borderRadius: "6px",
            width: "34px",
            height: "34px",
            padding: 0,
          }}
        >
          ✕
        </Button>
      </div>

      <div
        className="ArabicTextBlock mt-3"
        style={{
          flex: 1,
          overflowY: "auto",
          direction: "rtl",
          textAlign: "right",
          lineHeight: 1.9,
          fontSize: "15px",
          color: "#1a1a1a",
        }}
      >
        {hasText ? (
          <p>{textDisplayed}</p>
        ) : (
          <p style={{ direction: "ltr", textAlign: "left", color: "#6c757d" }}>
            No OCR text was found for this selected block.
          </p>
        )}
      </div>
    </Container>
  );
};

const FeedbackPane = ({
  title,
  currentPage,
  numberOfPages,
  selectedLanguage,
  feedbackType,
  feedbackDetails,
  onTypeChange,
  onDetailsChange,
  onCancel,
  onSubmit,
}) => (
  <aside className="FeedbackPaneInline" aria-label="Report issue">
    <div className="FeedbackPaneHeader">
      <h5>Report Issue</h5>
      <Button variant="link" size="sm" onClick={onCancel} aria-label="Close report issue panel">
        x
      </Button>
    </div>

    <div className="FeedbackSummary">
      <div>
        <strong>Title:</strong> {title || "Untitled"}
      </div>
      <div>
        <strong>Current page:</strong> {currentPage}
        {numberOfPages ? ` of ${numberOfPages}` : ""}
      </div>
      <div>
        <strong>Translation language:</strong> {selectedLanguage.toUpperCase()}
      </div>
    </div>

    <Form.Group className="mb-3" controlId="feedbackType">
      <Form.Label>What is the issue you are reporting?</Form.Label>
      <Form.Select value={feedbackType} onChange={(event) => onTypeChange(event.target.value)}>
        {FEEDBACK_TYPES.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Form.Select>
    </Form.Group>

    <Form.Group controlId="feedbackDetails">
      <Form.Label>Details</Form.Label>
      <Form.Control
        as="textarea"
        rows={6}
        placeholder="Tell us about the transcription/translation issue or any other feedback."
        value={feedbackDetails}
        onChange={(event) => onDetailsChange(event.target.value)}
      />
    </Form.Group>

    <div className="FeedbackPaneActions">
      <Button variant="outline-secondary" onClick={onCancel}>
        Cancel
      </Button>
      <Button variant="primary" onClick={onSubmit}>
        Submit
      </Button>
    </div>
  </aside>
);

const AnnotationLayer = ({
  highlights,
  onBoxClick,
  colorRotation,
  selectedBoxId,
  hoveredBoxId,
  onBoxHover,
}) => {
  if (!Array.isArray(highlights) || highlights.length === 0) return null;

  const getPolygonBounds = (polygon) => {
    const xs = polygon.map(({ x }) => x);
    const ys = polygon.map(({ y }) => y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  };

  return (
    <svg
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        zIndex: 2,
        pointerEvents: "none",
      }}
    >
      {highlights.map(({ id, polygon }) => {
        const bounds = getPolygonBounds(polygon);
        const numericId = Number(id);
        const colorIndex = Number.isFinite(numericId)
          ? Math.abs(numericId) % colorRotation.length
          : 0;

        const isHovered = hoveredBoxId === id;
        const isSelected = selectedBoxId === id;

        const strokeColor = isSelected
          ? "rgb(123, 150, 123)"
          : colorRotation[colorIndex];

        const fillColor = isSelected
          ? "rgba(128, 160, 128, 0.25)"
          : isHovered
          ? "rgba(160, 145, 145, 0.3)"
          : "transparent";

        return (
          <rect
            key={id}
            x={bounds.x}
            y={bounds.y}
            width={bounds.width}
            height={bounds.height}
            fill={fillColor}
            stroke={strokeColor}
            strokeWidth={1.5}
            vectorEffect="non-scaling-stroke"
            style={{ cursor: "pointer", pointerEvents: "auto" }}
            onClick={() => onBoxClick(id)}
            onMouseEnter={() => onBoxHover(id)}
            onMouseLeave={() => onBoxHover(null)}
          />
        );
      })}
    </svg>
  );
};

const normalizeLegacyCoord = (coord, imageSize) => {
  if (
    !Array.isArray(coord) ||
    coord.length < 6 ||
    coord.length % 2 !== 0 ||
    !imageSize?.width ||
    !imageSize?.height
  ) {
    return null;
  }

  const width = Number(imageSize.width);
  const height = Number(imageSize.height);
  const values = coord.map(Number);

  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }

  if (values.some((value) => !Number.isFinite(value))) {
    return null;
  }

  const polygon = [];
  for (let i = 0; i < values.length; i += 2) {
    polygon.push({ x: values[i] / width, y: values[i + 1] / height });
  }

  return polygon;
};

const getHighlightPolygon = (line, imageSize) => {
  if (Array.isArray(line?.polygon) && line.polygon.length >= 3) {
    return line.polygon
      .map((point) => ({
        x: Number(point.x),
        y: Number(point.y),
      }))
      .filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
  }

  const polygon = normalizeLegacyCoord(line?.coord, imageSize);
  if (!polygon) return null;

  return polygon.filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
};

const toOverlayHighlights = (lines, imageSize) =>
  (Array.isArray(lines) ? lines : [])
    .map((line, index) => {
      const polygon = getHighlightPolygon(line, imageSize);
      if (!Array.isArray(polygon) || polygon.length < 3) return null;

      return {
        ...line,
        id: line.id ?? index,
        text: line.text || "",
        polygon,
      };
    })
    .filter(Boolean);

const getStemFromUrl = (url) => {
  if (!url) return "";

  try {
    const parsedUrl = new URL(url);
    const filename = decodeURIComponent(parsedUrl.pathname.split("/").pop() || "");
    return filename.replace(/\.pdf$/i, "").toLowerCase();
  } catch {
    const filename = decodeURIComponent(url.split("?")[0].split("/").pop() || "");
    return filename.replace(/\.pdf$/i, "").toLowerCase();
  }
};

const normalizePdfUrl = (url) => {
  if (!url) return "";

  try {
    const parsedUrl = new URL(url);
    parsedUrl.pathname = parsedUrl.pathname.toLowerCase();
    return parsedUrl.toString();
  } catch {
    const [path, query] = url.split("?");
    return `${path.toLowerCase()}${query ? `?${query}` : ""}`;
  }
};

const withUppercaseFilename = (url) => {
  try {
    const parsedUrl = new URL(url);
    const segments = parsedUrl.pathname.split("/");
    const filename = segments.pop();
    const dotIdx = filename.lastIndexOf(".");
    const stem = dotIdx > -1 ? filename.slice(0, dotIdx) : filename;
    const ext = dotIdx > -1 ? filename.slice(dotIdx) : "";
    segments.push(`${stem.toUpperCase()}${ext.toLowerCase()}`);
    parsedUrl.pathname = segments.join("/");
    return parsedUrl.toString();
  } catch {
    return url;
  }
};

const pdfUrlExists = async (url) => {
  try {
    const res = await fetch(url, { headers: { Range: "bytes=0-0" } });
    return res.ok || res.status === 206;
  } catch {
    return false;
  }
};

// S3 object keys are case-sensitive. The app's convention is a lowercase
// filename, but some legacy collections were uploaded with an all-caps key,
// so fall back to that variant if the lowercase URL 403s.
const resolveCaseInsensitivePdfUrl = async (url) => {
  if (!url) return url;
  if (await pdfUrlExists(url)) return url;
  const upperUrl = withUppercaseFilename(url);
  if (upperUrl !== url && (await pdfUrlExists(upperUrl))) return upperUrl;
  return url;
};

// pdf.js error messages (e.g. "Unexpected server response (403) while
// retrieving PDF \"<url>\"") embed the raw S3 URL. Replace it with just the
// bucket and filename so the signed path/query string isn't exposed.
const maskS3UrlInMessage = (message) => {
  if (!message) return message;
  return message.replace(/"(https?:\/\/[^"]*)"/i, (fullMatch, url) => {
    try {
      const parsedUrl = new URL(url);
      if (!/\.amazonaws\.com$/i.test(parsedUrl.hostname)) return fullMatch;
      const bucket = parsedUrl.hostname.replace(/\.amazonaws\.com$/i, "");
      const filename = decodeURIComponent(parsedUrl.pathname.split("/").pop() || "");
      return `bucket ${bucket} and file ${filename}`;
    } catch {
      return "the requested file";
    }
  });
};

const renderPdfLoadError = (error) => (
  <div className="rpv-core__doc-error">
    <div className="rpv-core__doc-error-text">
      {maskS3UrlInMessage(error?.message) || "Failed to load the document."}
    </div>
  </div>
);

const PdfViewerModal = ({ show, handleClose, searchQuery, matchType, row }) => {
  const defaultLayoutPluginInstance2 = defaultLayoutPlugin();

  const toolbarPluginInstance = toolbarPlugin();
  const { renderDefaultToolbar } = toolbarPluginInstance;

  const transform = (slot) => ({
    ...slot,
    Download: () => <></>,
    Print: () => <></>,
    Open: () => <></>,
  });

  const defaultLayoutPluginInstance1 = defaultLayoutPlugin({
    renderToolbar: (Toolbar) => <Toolbar>{renderDefaultToolbar(transform)}</Toolbar>,
  });

  const pageNavigationPluginInstance = pageNavigationPlugin();
  const [maxPage, setMaxPage] = useState(row?.number_of_pages || 0);
  const [page, setPage] = useState(row?.page_number || 0);
  const [pdfUrl, setPDFurl] = useState(row?.AWS_link || "");
  const ocrSearch = useOcrSearch(pdfUrl, searchQuery, matchType, show);
  const pdfResolveTokenRef = useRef(0);

  const applyResolvedPdfUrl = async (candidateUrl) => {
    const token = ++pdfResolveTokenRef.current;
    const resolved = await resolveCaseInsensitivePdfUrl(candidateUrl);
    if (pdfResolveTokenRef.current === token) {
      setPDFurl(resolved);
    }
  };

  const [enteredPageNumber, setEnteredPageNumber] = useState("");
  const [copySuccess, setCopySuccess] = useState("");
  const [sidebarIsOpen, setSidebarIsOpen] = useState(false);
  const [selectedHighlightIdx, setSelectedHighlightIdx] = useState(null);
  const [selectedHighlight, setSelectedHighlight] = useState(null);
  const [selectedBoxId, setSelectedBoxId] = useState(null);
  const [hoveredBoxId, setHoveredBoxId] = useState(null);
  const [colorRotation] = useState(["#FF0000"]);
  const [showFeedbackPane, setShowFeedbackPane] = useState(false);
  const [feedbackType, setFeedbackType] = useState(FEEDBACK_TYPES[0].value);
  const [feedbackDetails, setFeedbackDetails] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("en");

  const {
    coll_type,
    coll_name,
    title,
    date,
    date_of_journal,
    date_of_publication,
    coll_location,
    place_of_publication,
    coll_source,
    publisher,
    issue_number,
    volume,
    authors,
  } = row || {};

  const segmentationFilename = getStemFromUrl(pdfUrl)?.toLowerCase();
  const displayedPageMatches = Number(segmentationFilename?.split("_").pop()) === Number(page);
  const segmentation = usePageSegmentation(segmentationFilename,
    show && displayedPageMatches && ["book", "periodical"].includes(coll_type));
  const highlights = toOverlayHighlights(segmentation.data?.lines || [], segmentation.data?.image_size || null);
  const translationContext = useRef(null);
  translationContext.current = `${segmentationFilename}:${segmentation.data?.segmentation_revision || ""}:${selectedHighlightIdx}`;

  const fixedDate = coll_type === "periodical" ? date || date_of_journal : date || date_of_publication;

  let citationText;

  if (coll_type === "periodical") {
    citationText = `${coll_name}, Volume ${volume}, Issue ${issue_number}, ${fixedDate}, Khayrallah Center for Lebanese Diaspora Studies arabicsearch.org, ${coll_source}`;
  } else {
    let authorsList = authors;
    if (typeof authors !== "string") authorsList = authors?.map((obj) => obj.author) || [];
    citationText = `${authorsList}, ${title}, (${place_of_publication}: ${publisher}, ${fixedDate}), Khayrallah Center for Lebanese Diaspora Studies arabicsearch.org`;
  }

  const isPreviousButtonDisabled = page === 1;
  const isNextButtonDisabled = page === maxPage;

  const convertArabicToNumber = (arabicNumeral) => {
    const map = {
      "٠": "0",
      "١": "1",
      "٢": "2",
      "٣": "3",
      "٤": "4",
      "٥": "5",
      "٦": "6",
      "٧": "7",
      "٨": "8",
      "٩": "9",
    };

    let number = "";
    for (const ch of arabicNumeral) number += map[ch] || ch;
    return parseInt(number);
  };

  const get_new_file = (currentPage) => {
    try {
      const url = new URL(pdfUrl);
      url.pathname = `${url.pathname.split("_").slice(0, -1).join("_")}_${currentPage}.pdf`.toLowerCase();
      return normalizePdfUrl(url.toString());
    } catch {
      return normalizePdfUrl(`${pdfUrl.split("_").slice(0, -1).join("_")}_${currentPage}.pdf`);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(citationText);
      setCopySuccess("Copied!");
      setTimeout(() => setCopySuccess(""), 2000);
    } catch {
      setCopySuccess("Failed to copy");
    }
  };

  const handlePageNumberChange = (e) => setEnteredPageNumber(e.target.value);

  const navigateToEnteredPage = () => {
    const pageNumber = convertArabicToNumber(enteredPageNumber);

    if (pageNumber >= 1 && pageNumber <= maxPage) {
      setPage(() => {
        applyResolvedPdfUrl(get_new_file(pageNumber));
        return pageNumber;
      });
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setSidebarIsOpen(false);
      setSelectedBoxId(null);
      setSelectedHighlightIdx(null);
      setSelectedHighlight(null);

      setPage((prev) => {
        const n = prev - 1;
        applyResolvedPdfUrl(get_new_file(n));
        return n;
      });
    }
  };

  const handleNextPage = () => {
    if (page < maxPage) {
      setSidebarIsOpen(false);
      setSelectedBoxId(null);
      setSelectedHighlightIdx(null);
      setSelectedHighlight(null);

      setPage((prev) => {
        const n = prev + 1;
        applyResolvedPdfUrl(get_new_file(n));
        return n;
      });
    }
  };

  const handleEnterKey = (e) => {
    if (e.key === "Enter") navigateToEnteredPage();
  };

  const handleCopyButtonClick = () => {
    navigator.clipboard.writeText(pdfUrl);
    toast.success("URL copied to clipboard!", { position: "top-right", autoClose: 3000 });
  };

  const handleCloseSidebar = () => {
    setSidebarIsOpen(false);
    setSelectedBoxId(null);
    setSelectedHighlightIdx(null);
    setSelectedHighlight(null);
  };

  const handleOpenSidebar = (idx) => {
    const found = highlights.find((h) => h.id === idx);
    if (!found) return;

    setSelectedBoxId(idx);
    setSidebarIsOpen(true);
    setSelectedHighlightIdx(idx);
    setSelectedHighlight(found);
  };

  const handleBoxHover = (idx) => setHoveredBoxId(idx);

  const handleOnClickTranslate = async (language_code) => {
    if (!selectedHighlight) return;

    const normalizedLanguage = language_code.toLowerCase();
    setSelectedLanguage(normalizedLanguage);
    const key = `translation_${normalizedLanguage}`;

    if (key in selectedHighlight) return selectedHighlight[key];

    const fileName = segmentationFilename;
    const context = translationContext.current;

    try {
      const response = await segmentationAPI.post("translate", {
        blockId: selectedHighlightIdx,
        fileName,
        target_language: normalizedLanguage,
        revision: segmentation.data?.segmentation_revision || null,
      });

      if (translationContext.current === context) return response.data.text;
    } catch (error) {
      console.log("Translation Failed");
    }
  };

  const handleOpenFeedbackPane = () => {
    setFeedbackType(FEEDBACK_TYPES[0].value);
    setFeedbackDetails("");
    setShowFeedbackPane(true);
  };

  const handleFeedbackCancel = () => {
    setShowFeedbackPane(false);
  };

  const handleCloseModal = () => {
    setShowFeedbackPane(false);
    setFeedbackDetails("");
    setFeedbackType(FEEDBACK_TYPES[0].value);
    handleClose();
  };

  const getDocumentId = () =>
    row?._id || row?.id || getStemFromUrl(pdfUrl) || row?.AWS_link || `${row?.coll_identifier || "document"}_${page}`;

  const handleFeedbackSubmit = async () => {
    if (!feedbackDetails.trim()) {
      toast.warn("Please provide details for the issue you are reporting.", {
        position: "top-center",
        autoClose: 2500,
        toastId: TOAST_IDS.feedbackMissingDetails,
      });
      return;
    }

    const feedbackPayload = {
      feedbackType,
      details: feedbackDetails.trim(),
      pageNumber: page || row?.page_number || 1,
      document: {
        id: getDocumentId(),
      },
    };

    try {
      await feedbackAPI.post("/", feedbackPayload);
      toast.success("Feedback submitted successfully.", {
        position: "top-center",
        autoClose: 2500,
        toastId: TOAST_IDS.feedbackSuccess,
      });
      setShowFeedbackPane(false);
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      toast.error("Failed to submit feedback.", {
        position: "top-center",
        autoClose: 3000,
        toastId: TOAST_IDS.feedbackError,
      });
    }
  };

  useEffect(() => {
    if (row) {
      setMaxPage(row.number_of_pages);
      setPage(row.page_number || 1);
      applyResolvedPdfUrl(normalizePdfUrl(row.AWS_link || ""));

      setSelectedBoxId(null);
      setSelectedHighlightIdx(null);
      setSelectedHighlight(null);
      setSidebarIsOpen(false);
      setShowFeedbackPane(false);
      setFeedbackDetails("");
      setFeedbackType(FEEDBACK_TYPES[0].value);
    }
  }, [row]);

  useEffect(() => {
    setSelectedBoxId(null);
    setSelectedHighlightIdx(null);
    setSelectedHighlight(null);
    setSidebarIsOpen(false);
  }, [page, pdfUrl]);

  const viewerPlugins = (() => {
    const layoutPlugin = row?.is_download_blocked ? defaultLayoutPluginInstance1 : defaultLayoutPluginInstance2;
    const plugins = [layoutPlugin, pageNavigationPluginInstance];

    return plugins;
  })();

  return (
    <Modal show={show} onHide={handleCloseModal} dialogClassName="my-modal">
      <Modal.Body closeButton style={{ justifyContent: "space-between" }}>
        <OcrSearchStatus search={ocrSearch} query={searchQuery} mode={matchType} />
        {segmentation.status === "failed" && (
          <div role="alert" className="alert alert-warning">Segmentation could not be loaded. Reopen this page to retry.</div>
        )}
        <div className="container mt-3">
          <div className="row">
            <div className="col-lg-auto ColStyle">
              <div className="CollNameStyle">{coll_type === "periodical" ? coll_name : title}</div>
            </div>

            <div className="col-md-auto ColStyle">
              <Calender className="IconStyle" />
              <div className="DataStyle">{coll_type === "periodical" ? fixedDate : date_of_publication}</div>
            </div>

            <div className="col-md-auto ColStyle">
              <Compass className="IconStyle" />
              <div className="DataStyle">{coll_type === "periodical" ? coll_location : place_of_publication}</div>
            </div>

            <div className="col-md-auto ColStyle">
              <Multiple className="IconStyle" />
              <div className="DataStyle">{coll_type === "periodical" ? coll_source : publisher}</div>
            </div>

            <div className="col-sm-auto ColStyle" style={{ justifyContent: "end" }}>
              <Button disabled={isNextButtonDisabled} onClick={handleNextPage} className="ButtonStyle">
                <FontAwesomeIcon icon={faChevronLeft} />
                <span style={{ paddingLeft: "7px" }}>Next</span>
              </Button>
            </div>

            <div className="col-sm-auto ColStyle" style={{ justifyContent: "center" }}>
              <Document className="IconStyle" />
              <div className="DataStyle">Page {page} of {maxPage}</div>
            </div>

            <div className="col-sm-auto ColStyle" style={{ justifyContent: "start" }}>
              <Button disabled={isPreviousButtonDisabled} onClick={handlePreviousPage} className="ButtonStyle">
                <span style={{ paddingRight: "7px" }}>Previous</span>
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>

            <div className="col-sm-auto ColStyle" style={{ justifyContent: "end" }}>
              <Button onClick={handleCopyButtonClick} className="CopyButtonStyle">
                <span style={{ paddingRight: "5px" }}>Copy URL</span>
              </Button>
            </div>

            <div className="col-md-auto ColStyle">
              <label style={{ marginRight: "0.6rem" }}>Go to page</label>
              <input
                type="text"
                value={enteredPageNumber}
                onChange={handlePageNumberChange}
                onKeyDown={handleEnterKey}
                className="PageNumberInput"
              />
            </div>

            <div className="col ColStyle" style={{ justifyContent: "end" }}>
              <Close onClick={handleCloseModal} className="CloseBtnStyle" />
            </div>
          </div>

          <div className="row">
            <strong>Citation:</strong>
            <span>
              {citationText}
              <Button style={{ marginLeft: "0.5rem" }} onClick={copyToClipboard} className="CopyButtonStyle">
                <span style={{ paddingRight: "5px" }}>Copy Citation</span>
              </Button>
              {copySuccess && <span style={{ color: "green", marginLeft: "10px" }}>{copySuccess}</span>}
            </span>
          </div>
        </div>

        {pdfUrl && (
          <div className="container mt-3 PdfContentRow">
            {sidebarIsOpen &&
              selectedHighlightIdx !== null &&
              selectedHighlight && (
                <div className="TranslationPaneInline">
                  <TranslationModal
                    text={selectedHighlight.text || ""}
                    onClose={handleCloseSidebar}
                    onClickTranslate={handleOnClickTranslate}
                  />
                </div>
              )}

            <div
              style={{
                flex: "1 1 auto",
                minWidth: 0,
                transition: sidebarIsOpen ? "width 0.2s ease-in-out" : "",
                transformOrigin: sidebarIsOpen ? "left" : "",
              }}
            >
              <Worker workerUrl={process.env.REACT_APP_PDF_WORKER}>
                <Viewer
                  key={JSON.stringify([pdfUrl, searchQuery, matchType])}
                  theme="dark"
                  fileUrl={pdfUrl}
                  renderError={renderPdfLoadError}
                  plugins={viewerPlugins}
                  renderPage={(props) => (
                    <div
                      style={{
                        position: "relative",
                        width: `${props.width}px`,
                        height: `${props.height}px`,
                      }}
                    >
                      {props.canvasLayer.children}
                      {props.textLayer.children}
                      {props.annotationLayer.children}
                      <OcrSearchHighlights search={ocrSearch} {...props} />

                      {props.pageIndex === 0 && (
                        <AnnotationLayer
                          highlights={highlights}
                          onBoxClick={handleOpenSidebar}
                          colorRotation={colorRotation}
                          selectedBoxId={selectedBoxId}
                          hoveredBoxId={hoveredBoxId}
                          onBoxHover={handleBoxHover}
                        />
                      )}
                    </div>
                  )}
                />
              </Worker>
            </div>

            {showFeedbackPane && (
              <FeedbackPane
                title={coll_type === "periodical" ? coll_name : title}
                currentPage={page}
                numberOfPages={maxPage}
                selectedLanguage={selectedLanguage}
                feedbackType={feedbackType}
                feedbackDetails={feedbackDetails}
                onTypeChange={setFeedbackType}
                onDetailsChange={setFeedbackDetails}
                onCancel={handleFeedbackCancel}
                onSubmit={handleFeedbackSubmit}
              />
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="FooterStyle">
        <Button className="FeedbackButton" onClick={handleOpenFeedbackPane}>
          Report Issue
        </Button>
        <Button className="CloseButton" onClick={handleCloseModal}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PdfViewerModal;
